import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, CheckCircle, Delete, Edit, Refresh, Search } from '@mui/icons-material';
import CustomPagination from '../../components/common/CustomPagination';
import {
  bulkDeletePlatformTranslations,
  bulkUpdatePlatformTranslations,
  createPlatformTranslation,
  deletePlatformTranslation,
  getPlatformTranslations,
  markPlatformTranslationReviewed,
  updatePlatformTranslation,
} from '../../services/searchAdmin';
import CategoryTreeSelector from '../../components/CategoryTreeSelector';

const defaultForm = {
  entity_type: '',
  entity_id: '',
  field_name: '',
  language_code: 'zh',
  context: '',
  source_text: '',
  translated_text: '',
  notes: '',
};

const PlatformTranslations = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    entity_type: '',
    language_code: 'zh',
    is_reviewed: '',
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [categorySelectorOpen, setCategorySelectorOpen] = useState(false);
  const [selectedTranslationCategory, setSelectedTranslationCategory] = useState(null);
  const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkAction, setBulkAction] = useState('update');
  const [bulkForm, setBulkForm] = useState({
    mode: 'match',
    ids: [],
    source_text: '',
    current_translated_text: '',
    translated_text: '',
    language_code: 'zh',
    entity_type: '',
    field_name: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPlatformTranslations({
        ...filters,
        page: page + 1,
        page_size: rowsPerPage,
      });
      setRows(response.results || []);
      setTotalPages(response.total_pages || 0);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleRowsPerPageChange = (event) => {
    const value = event.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(String(rowsPerPage));
      return;
    }
    setRowsPerPage(parseInt(value, 10));
    setCustomRowsActive(false);
    setPage(0);
  };

  const handleCustomRowsInputBlur = () => {
    const parsedValue = parseInt(customRowsInput, 10);
    if (Number.isNaN(parsedValue) || parsedValue <= 0) {
      setCustomRowsInput(String(rowsPerPage));
      return;
    }
    setRowsPerPage(parsedValue);
    setCustomRowsActive(false);
    setPage(0);
  };

  const openDialog = useCallback((row = null) => {
    setEditing(row);
    const isCategoryBacked = ['schema', 'category'].includes((row?.entity_type || '').toLowerCase());
    setSelectedTranslationCategory(isCategoryBacked && row?.entity_id ? {
      sid: row.entity_id,
      id: row.entity_id,
      name: row.entity_id,
    } : null);
    setForm(row ? {
      entity_type: row.entity_type || '',
      entity_id: row.entity_id || '',
      field_name: row.field_name || '',
      language_code: row.language_code || 'zh',
      context: row.context || '',
      source_text: row.source_text || '',
      translated_text: row.translated_text || '',
      notes: row.notes || '',
    } : defaultForm);
    setDialogOpen(true);
  }, []);

  const closeDialog = () => {
    setDialogOpen(false);
    setCategorySelectorOpen(false);
    setEditing(null);
    setForm(defaultForm);
    setSelectedTranslationCategory(null);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await updatePlatformTranslation(editing.id, form);
        setSnackbar({ open: true, message: 'Translation updated', severity: 'success' });
      } else {
        await createPlatformTranslation(form);
        setSnackbar({ open: true, message: 'Translation created', severity: 'success' });
      }
      closeDialog();
      fetchRows();
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const handleCategorySelect = useCallback((category) => {
    setSelectedTranslationCategory(category);
    setForm((prev) => ({
      ...prev,
      entity_id: String(category.sid || category.id || ''),
    }));
    setCategorySelectorOpen(false);
  }, []);

  const handleDelete = useCallback(async (row) => {
    try {
      await deletePlatformTranslation(row.id);
      setSnackbar({ open: true, message: 'Translation deleted', severity: 'info' });
      fetchRows();
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  }, [fetchRows]);

  const handleReview = useCallback(async (row) => {
    try {
      await markPlatformTranslationReviewed(row.id);
      setSnackbar({ open: true, message: 'Translation marked reviewed', severity: 'success' });
      fetchRows();
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  }, [fetchRows]);

  const handleInlineUpdate = useCallback(async (newRow, oldRow) => {
    if ((newRow.translated_text || '') === (oldRow.translated_text || '')) {
      return oldRow;
    }

    const updated = await updatePlatformTranslation(newRow.id, {
      translated_text: newRow.translated_text || '',
    });
    setRows((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
    setSnackbar({ open: true, message: 'Translation updated', severity: 'success' });
    return updated;
  }, []);

  const getSelectedIds = useCallback(() => {
    if (Array.isArray(rowSelectionModel)) {
      return rowSelectionModel;
    }
    if (rowSelectionModel?.ids instanceof Set) {
      return Array.from(rowSelectionModel.ids);
    }
    if (rowSelectionModel?.ids) {
      return Array.from(rowSelectionModel.ids);
    }
    return [];
  }, [rowSelectionModel]);

  const updateBulkFormField = useCallback((field, value) => {
    setBulkPreview(null);
    setBulkForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const openBulkDialog = useCallback((mode, row = null, action = 'update') => {
    const selectedIds = getSelectedIds();
    setBulkPreview(null);
    setBulkAction(action);
    setBulkForm({
      mode,
      ids: mode === 'selected' ? selectedIds : [],
      source_text: row?.source_text || filters.search || '',
      current_translated_text: row?.translated_text || '',
      translated_text: row?.translated_text || '',
      language_code: row?.language_code || filters.language_code || 'zh',
      entity_type: '',
      field_name: '',
    });
    setBulkDialogOpen(true);
  }, [filters.language_code, filters.search, getSelectedIds]);

  const closeBulkDialog = () => {
    setBulkDialogOpen(false);
    setBulkPreview(null);
  };

  const buildBulkPayload = useCallback((dryRun) => {
    const payload = {
      mode: bulkForm.mode,
      dry_run: dryRun,
    };

    if (bulkAction === 'update') {
      payload.translated_text = bulkForm.translated_text;
    }

    if (bulkForm.mode === 'selected') {
      payload.ids = bulkForm.ids;
    } else {
      payload.source_text = bulkForm.source_text;
      payload.language_code = bulkForm.language_code;
      payload.current_translated_text = bulkForm.current_translated_text;
      if (bulkForm.entity_type) payload.entity_type = bulkForm.entity_type;
      if (bulkForm.field_name) payload.field_name = bulkForm.field_name;
    }

    return payload;
  }, [bulkAction, bulkForm]);

  const handleBulkPreview = async () => {
    try {
      setBulkPreviewLoading(true);
      const action = bulkAction === 'delete' ? bulkDeletePlatformTranslations : bulkUpdatePlatformTranslations;
      const response = await action(buildBulkPayload(true));
      setBulkPreview(response);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setBulkPreviewLoading(false);
    }
  };

  const handleBulkApply = async () => {
    try {
      setBulkSaving(true);
      const action = bulkAction === 'delete' ? bulkDeletePlatformTranslations : bulkUpdatePlatformTranslations;
      const response = await action(buildBulkPayload(false));
      const changedCount = bulkAction === 'delete' ? response.deleted : response.updated;
      setSnackbar({
        open: true,
        message: `${changedCount || 0} translations ${bulkAction === 'delete' ? 'deleted' : 'updated'}`,
        severity: bulkAction === 'delete' ? 'info' : 'success',
      });
      closeBulkDialog();
      setRowSelectionModel({ type: 'include', ids: new Set() });
      fetchRows();
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setBulkSaving(false);
    }
  };

  const columns = useMemo(() => [
    { field: 'entity_type', headerName: 'Entity', minWidth: 130, flex: 0.7 },
    { field: 'field_name', headerName: 'Field', minWidth: 130, flex: 0.7 },
    { field: 'language_code', headerName: 'Lang', width: 90 },
    {
      field: 'source_text',
      headerName: 'Source Text',
      minWidth: 260,
      flex: 1.2,
    },
    {
      field: 'translated_text',
      headerName: 'Translated Text',
      minWidth: 260,
      flex: 1.2,
      editable: true,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 145,
      renderCell: ({ row }) => (
        <Chip
          label={row.is_reviewed ? 'Reviewed' : row.source === 'machine' ? 'Machine Draft' : 'Pending'}
          color={row.is_reviewed ? 'success' : 'warning'}
          size="small"
        />
      ),
    },
    {
      field: 'context',
      headerName: 'Context',
      minWidth: 180,
      flex: 0.8,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 190,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {!row.is_reviewed && (
            <Tooltip title="Mark reviewed">
              <IconButton size="small" color="success" onClick={() => handleReview(row)}>
                <CheckCircle />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <IconButton size="small" color="primary" onClick={() => openDialog(row)}>
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Apply this correction to exact matches">
            <IconButton size="small" color="secondary" onClick={() => openBulkDialog('match', row, 'update')}>
              <CheckCircle />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete exact matches">
            <IconButton size="small" color="warning" onClick={() => openBulkDialog('match', row, 'delete')}>
              <Delete />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ], [handleDelete, handleReview, openBulkDialog, openDialog]);

  const selectedCount = getSelectedIds().length;
  const usesCategoryEntity = ['schema', 'category'].includes((form.entity_type || '').toLowerCase());

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden',
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          Translation Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<Refresh />} variant="outlined" onClick={fetchRows} disabled={loading}>Refresh</Button>
          <Button
            startIcon={<CheckCircle />}
            variant="outlined"
            color="secondary"
            disabled={selectedCount === 0}
            onClick={() => openBulkDialog('selected', null, 'update')}
          >
            Update Selected ({selectedCount})
          </Button>
          <Button
            startIcon={<Delete />}
            variant="outlined"
            color="error"
            disabled={selectedCount === 0}
            onClick={() => openBulkDialog('selected', null, 'delete')}
          >
            Delete Selected ({selectedCount})
          </Button>
          <Button startIcon={<Add />} variant="contained" onClick={() => openDialog()}>Add</Button>
        </Box>
      </Box>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search source, translation, key..."
            value={filters.search}
            onChange={(event) => handleFilterChange('search', event.target.value)}
            InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 2 }}>
          <TextField select fullWidth size="small" label="Entity" value={filters.entity_type} onChange={(event) => handleFilterChange('entity_type', event.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="category">Category</MenuItem>
            <MenuItem value="schema">Schema</MenuItem>
            <MenuItem value="brand">Brand</MenuItem>
            <MenuItem value="status">Status</MenuItem>
          </TextField>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <TextField select fullWidth size="small" label="Language" value={filters.language_code} onChange={(event) => handleFilterChange('language_code', event.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="zh">Chinese</MenuItem>
            <MenuItem value="en">English</MenuItem>
          </TextField>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <TextField select fullWidth size="small" label="Review" value={filters.is_reviewed} onChange={(event) => handleFilterChange('is_reviewed', event.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="false">Needs Review</MenuItem>
            <MenuItem value="true">Reviewed</MenuItem>
          </TextField>
        </Grid>
      </Grid>

      <Box sx={{ width: '100%', overflowX: 'auto', bgcolor: 'background.paper' }}>
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          loading={loading}
          hideFooter
          disableRowSelectionOnClick
          checkboxSelection
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={setRowSelectionModel}
          processRowUpdate={handleInlineUpdate}
          onProcessRowUpdateError={(error) => setSnackbar({ open: true, message: error.message, severity: 'error' })}
          sx={{
            minWidth: 1180,
            border: 'none',
            '& .MuiDataGrid-columnHeader': { backgroundColor: 'primary.main' },
            '& .MuiDataGrid-columnHeaderTitle': { color: 'primary.contrastText', fontWeight: 'bold' },
            '& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton': { color: 'primary.contrastText' },
          }}
        />
      </Box>

      <CustomPagination
        page={page}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        customRowsActive={customRowsActive}
        customRowsInput={customRowsInput}
        onCustomRowsInputChange={(event) => setCustomRowsInput(event.target.value)}
        onCustomRowsInputBlur={handleCustomRowsInputBlur}
      />

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Translation' : 'Add Translation'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Entity Type"
                value={form.entity_type}
                onChange={(event) => {
                  const nextEntityType = event.target.value;
                  setForm((prev) => ({ ...prev, entity_type: nextEntityType }));
                  if (!['schema', 'category'].includes(nextEntityType.toLowerCase())) {
                    setSelectedTranslationCategory(null);
                  }
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {usesCategoryEntity ? (
                <Box>
                  <TextField
                    fullWidth
                    label="Entity ID"
                    value={form.entity_id}
                    InputProps={{ readOnly: true }}
                    helperText={selectedTranslationCategory?.name && selectedTranslationCategory.name !== form.entity_id
                      ? `Selected: ${selectedTranslationCategory.name}`
                      : 'Select a category; its SID will be saved as Entity ID.'}
                  />
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setCategorySelectorOpen(true)}
                    sx={{ mt: 1 }}
                  >
                    {form.entity_id ? 'Change Category' : 'Select Category'}
                  </Button>
                </Box>
              ) : (
                <TextField fullWidth label="Entity ID" value={form.entity_id} onChange={(event) => setForm((prev) => ({ ...prev, entity_id: event.target.value }))} />
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Field Name" value={form.field_name} onChange={(event) => setForm((prev) => ({ ...prev, field_name: event.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Language" value={form.language_code} onChange={(event) => setForm((prev) => ({ ...prev, language_code: event.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Context" value={form.context} onChange={(event) => setForm((prev) => ({ ...prev, context: event.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth multiline minRows={3} label="Source Text" value={form.source_text} onChange={(event) => setForm((prev) => ({ ...prev, source_text: event.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth multiline minRows={3} label="Translated Text" value={form.translated_text} onChange={(event) => setForm((prev) => ({ ...prev, translated_text: event.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth multiline minRows={2} label="Notes" value={form.notes} onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDialogOpen} onClose={closeBulkDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {bulkAction === 'delete'
            ? (bulkForm.mode === 'selected' ? 'Bulk Delete Selected Translations' : 'Bulk Delete Exact Matches')
            : (bulkForm.mode === 'selected' ? 'Bulk Update Selected Translations' : 'Bulk Update Exact Matches')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {bulkForm.mode === 'selected' ? (
              <Grid size={{ xs: 12 }}>
                <Alert severity={bulkAction === 'delete' ? 'warning' : 'info'}>
                  {bulkAction === 'delete'
                    ? `This will permanently delete ${bulkForm.ids.length} selected translation rows.`
                    : `This will apply the new translated text to ${bulkForm.ids.length} selected translation rows.`}
                </Alert>
              </Grid>
            ) : (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Language"
                    value={bulkForm.language_code}
                    onChange={(event) => updateBulkFormField('language_code', event.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Entity Type (optional)"
                    value={bulkForm.entity_type}
                    onChange={(event) => updateBulkFormField('entity_type', event.target.value)}
                    helperText="Leave empty to update all entity types with the same source text."
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Field Name (optional)"
                    value={bulkForm.field_name}
                    onChange={(event) => updateBulkFormField('field_name', event.target.value)}
                    helperText="Leave empty to update every field name with this source text."
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Current Translation (optional safeguard)"
                    value={bulkForm.current_translated_text}
                    onChange={(event) => updateBulkFormField('current_translated_text', event.target.value)}
                    helperText={`When filled, only rows with this current translation are ${bulkAction === 'delete' ? 'deleted' : 'updated'}.`}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Exact Source Text"
                    value={bulkForm.source_text}
                    onChange={(event) => updateBulkFormField('source_text', event.target.value)}
                  />
                </Grid>
              </>
            )}
            {bulkAction === 'update' && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="New Translated Text"
                  value={bulkForm.translated_text}
                  onChange={(event) => updateBulkFormField('translated_text', event.target.value)}
                />
              </Grid>
            )}
            {bulkPreview && (
              <Grid size={{ xs: 12 }}>
                <Alert severity={bulkPreview.count > 0 ? (bulkAction === 'delete' ? 'error' : 'warning') : 'info'}>
                  {bulkPreview.count || 0} translation rows match this {bulkAction}.
                </Alert>
                {bulkPreview.sample?.length > 0 && (
                  <Box sx={{ mt: 1.5, maxHeight: 220, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    {bulkPreview.sample.map((item) => (
                      <Box key={item.id} sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">
                          #{item.id} {item.entity_type} / {item.field_name} / {item.language_code}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {item.source_text}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Current: {item.translated_text || '(empty)'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBulkDialog} disabled={bulkSaving}>Cancel</Button>
          <Button onClick={handleBulkPreview} disabled={bulkPreviewLoading || bulkSaving}>
            {bulkPreviewLoading ? 'Checking...' : 'Preview Matches'}
          </Button>
          <Button
            variant="contained"
            color={bulkAction === 'delete' ? 'error' : 'warning'}
            onClick={handleBulkApply}
            disabled={bulkSaving || !bulkPreview || bulkPreview.count === 0}
          >
            {bulkSaving ? (bulkAction === 'delete' ? 'Deleting...' : 'Updating...') : (bulkAction === 'delete' ? 'Delete Matches' : 'Apply Update')}
          </Button>
        </DialogActions>
      </Dialog>

      <CategoryTreeSelector
        open={categorySelectorOpen}
        onClose={() => setCategorySelectorOpen(false)}
        onSelect={handleCategorySelect}
        selectedCategory={selectedTranslationCategory}
        title="Select Category for Translation"
      />

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlatformTranslations;
